<template>
    <div ref="rootRef"
         @click="handleVideoClick"
         class="relative w-full bg-zinc-950 flex-shrink-0 overflow-hidden touch-none"
         :class="{ 'z-50': shouldVideoBeLocked }"
         :style="{
         ...containerStyle,
         ...posterBackgroundStyle,
         transform: 'translate3d(0, 0, 0)',
         willChange: 'transform'
     }">
        <!-- Video Background -->
        <div class="absolute inset-0">
            <!-- Locked State: the feed draws its own lock card (ClipsLockOverlay) above the track -->
            <div
                v-if="shouldVideoBeLocked"
                class="relative z-[1] mx-auto object-center h-full w-full"
                :class="mediaFitClass"
            >
                <img
                    :src="lockPosterImageUrl"
                    :alt="videoData.title || 'Locked video'"
                    class="w-full h-full"
                    :class="mediaFitClass"
                />
            </div>

            <img
                v-if="!shouldVideoBeLocked && posterImageUrl"
                :src="posterImageUrl"
                :alt="videoData.title || ''"
                fetchpriority="high"
                :width="posterIntrinsicWidth"
                :height="posterIntrinsicHeight"
                :class="[
                    'absolute inset-0 z-[1] mx-auto h-full w-full object-center pointer-events-none',
                    mediaFitClass,
                ]"
                style="transform: translateZ(0); backface-visibility: hidden"
            />

            <!-- Video layer on top of poster: fades IN so we never reveal a black frame -->
            <div
                v-if="!shouldVideoBeLocked && renderVideo"
                :class="[
                    'absolute inset-0 z-[5] bg-zinc-950 transition-opacity duration-300 ease-out',
                    videoLayerVisible ? 'opacity-100' : 'opacity-0'
                ]"
                style="transform: translateZ(0); backface-visibility: hidden"
            >
                <video
                    ref="videoRef"
                    :aria-label="videoData?.title || undefined"
                    loop
                    playsinline
                    webkit-playsinline
                    disablePictureInPicture
                    crossorigin="anonymous"
                    controlsList="nodownload"
                    :poster="posterImageUrl"
                    :preload="preloadMode || (shouldAutoplay ? 'auto' : 'metadata')"
                    disableremoteplayback
                    :autoplay="shouldAutoplay"
                    :muted="isMuted || shouldVideoBeLocked"
                    @waiting="isBuffering = shouldAutoplay"
                    @canplay="isBuffering = false"
                    @loadstart="isBuffering = shouldAutoplay"
                    @loadedmetadata="clearPlaybackStallTimer"
                    @play="onVideoPlay"
                    @playing="onVideoPlaying"
                    @pause="isPlaying = false"
                    @error="onVideoError"
                    :class="[
                        'relative mx-auto object-center h-full w-full',
                        mediaFitClass,
                    ]">

                    <source
                        :src="progressiveSrc"
                        type="video/mp4"
                        @error="onProgressiveSourceError"
                    />
                </video>
            </div>

            <!-- Buffering Spinner -->
            <div
                v-if="isBuffering && !playbackFailed"
                class="absolute inset-0 flex items-center justify-center pointer-events-none z-20"
            >
                <div class="loading spinner">
                    <span class="text-white"></span>
                </div>
            </div>

            <!-- Play Indicator -->
            <div
                v-if="(isPaused || autoplayBlocked) && !shouldVideoBeLocked && !playbackFailed"
                data-testid="play-indicator"
                class="absolute inset-0 flex items-center justify-center pointer-events-none z-10 text-white"
            >
                <div class="w-12 h-12 rounded-full bg-white/95 flex items-center justify-center shadow-2xl">
                    <Play class="w-5 h-5 text-black ml-0.5" fill="black" />
                </div>
            </div>

            <!-- Playback failed: without this the slide just stays black -->
            <div
                v-if="playbackFailed && !shouldVideoBeLocked"
                role="alert"
                data-testid="playback-failed"
                class="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-black/60 px-6 text-center text-white"
                @click.stop
            >
                <p class="text-sm font-medium">Couldn't load this clip</p>
                <button
                    type="button"
                    class="rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-black active:scale-95 transition-transform"
                    @click.stop="retryPlayback"
                >
                    {{ playbackRetried ? 'Reload page' : 'Retry' }}
                </button>
            </div>
        </div>
    </div>
</template>

<script setup>
// Forked from Components/Reels/VideoPlayer.vue (NEX-9360) so the clips feed can change its
// player without touching the one Reels/auditions loads. Everything Reels-only is gone: the
// audition analytics, the stars/controls/overlay slots, the in-production badge and the lock card.
import { ref, computed, onMounted, watch, onUnmounted, inject, nextTick } from 'vue'
import { Play } from 'lucide-vue-next'
import { usePage } from '@inertiajs/vue3'
import { useResizeObserver } from '@vueuse/core'
import { useMutedStore } from '@/store/useMutedStore'
import { useClipAnalytics } from '@/composables/useClipAnalytics'
import {
    createCappedLoader,
    forcedCodec,
    isHlsDebugEnabled,
    recordHlsDebugEvent,
    registerHlsDebug,
} from '@/Helpers/clipHlsDebug'
import { createStallWatchdog } from '@/Helpers/clipHlsStallWatchdog'
import {
    buildClipHlsConfig,
    canDecodeHevc,
    capLevelForPlayer,
    currentSlidePlaying,
    mseAvailable,
    PREFETCH_MAX_BUFFER_SECONDS,
    preferNativeHls,
    prefetchAllowed,
    recalledBandwidthEstimate,
    rememberBandwidthEstimate,
} from '@/Helpers/clipHls'

const props = defineProps({
    videoData: {
        type: Object,
        default: () => ({})
    },
    routePrefix: {
        type: String
    },
    shouldAutoplay: {
        type: Boolean,
        default: false
    },
    shouldVideoBeLocked: {
        type: Boolean,
        default: false
    },
    // '' keeps the default preload (auto for the autoplaying slide, metadata otherwise).
    preloadMode: {
        type: String,
        default: ''
    },
    renderVideo: {
        type: Boolean,
        default: true
    },
    // The feed's next slide: may buffer one HLS fragment once the current clip plays.
    prefetchHls: {
        type: Boolean,
        default: false
    },
    // The feed-wide quality override from the non-production picker: a rendition height, or
    // null for hls.js's own ABR (the only mode production ever uses).
    preferredHeight: {
        type: Number,
        default: null
    }
})

const hasInteracted = inject('hasInteracted')
const page = usePage()
const mutedStore = useMutedStore()
const { trackClipPlaybackFailed } = useClipAnalytics({ routePrefix: props.routePrefix })

const rootRef = ref(null)
const videoRef = ref(null)
const isPaused = ref(false)
const videoProgress = ref(0)
const isMuted = ref(true)
const isPlaying = ref(false)
const isBuffering = ref(false)
// The current slide's play() was refused (NotAllowedError), so a still frame needs a tap cue.
// Set only from playVideo(), never derived from !isPlaying: every neighbour is not playing too.
const autoplayBlocked = ref(false)

let progressInterval = null

const windowHeight = ref(window.innerHeight)
const windowWidth = ref(window.innerWidth)

// Landscape key first: the clips feed emits exactly one poster key, matching the clip's
// orientation.
const posterImageUrl = computed(() =>
    props.videoData?.start_card_video_images?.image_1066x600_webp
    ?? props.videoData?.start_card_video_images?.image_600x1066_webp
    ?? null
)

const lockPosterImageUrl = computed(() =>
    props.videoData?.lock?.start_card_video_images?.image_1066x600_webp
    ?? props.videoData?.lock?.start_card_video_images?.image_600x1066_webp
    ?? null
)

const hasApiDimensions = computed(() => {
    const width = Number(props.videoData?.width)
    const height = Number(props.videoData?.height)

    return Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0
})

// One computed for both layers so the poster -> video crossfade can never shift the geometry.
const mediaFitClass = computed(() => props.videoData.is_vertical ? 'object-cover' : 'object-contain')

// Intrinsic size for the poster <img>, so the browser reserves the box before the bytes land.
const posterIntrinsicWidth = computed(() => hasApiDimensions.value ? Number(props.videoData.width) : null)
const posterIntrinsicHeight = computed(() => hasApiDimensions.value ? Number(props.videoData.height) : null)

// Poster fast-path: the same URL rides the root wrapper as a background-image, the <img> and
// the <video poster> attribute — one fetch, three chances to paint a first frame before any
// video byte arrives. It sits on the wrapper, *behind* the video layer, so the video layer's
// opaque bg-zinc-950 (the letterbox-bleed fix) still covers it once the crossfade completes.
const posterBackgroundStyle = computed(() => {
    if (props.shouldVideoBeLocked || !posterImageUrl.value) {
        return {}
    }

    return {
        backgroundImage: `url("${posterImageUrl.value}")`,
        // Mirror the poster <img>'s object-fit so the two layers never disagree on geometry.
        backgroundSize: mediaFitClass.value === 'object-cover' ? 'cover' : 'contain',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
    }
})

// The mp4 failed (a 404, or a 474 once an IP-bound URL outlives the viewer's IP — wifi to
// cellular is enough), or the element never got as far as metadata. Neither reaches
// onVideoError: a failed <source> fires `error` on the <source>, not the <video>, and Chrome
// hangs on a 474 without firing one at all, hence the stall timer.
const PLAYBACK_STALL_MS = 15000
const playbackFailed = ref(false)
const playbackRetried = ref(false)
let playbackStallTimer = null

function clearPlaybackStallTimer() {
    clearTimeout(playbackStallTimer)
    playbackStallTimer = null
}

const markPlaybackFailed = (reason) => {
    if (playbackFailed.value) {
        return
    }

    clearPlaybackStallTimer()
    playbackFailed.value = true
    isBuffering.value = false

    trackClipPlaybackFailed(reason, playbackRetried.value, props.videoData)
}

const armPlaybackStallTimer = () => {
    clearPlaybackStallTimer()

    if (!props.shouldAutoplay || props.shouldVideoBeLocked) {
        return
    }

    playbackStallTimer = setTimeout(() => {
        const video = videoRef.value

        if (video && video.readyState === HTMLMediaElement.HAVE_NOTHING) {
            markPlaybackFailed('stall')
        }
    }, PLAYBACK_STALL_MS)
}

// A <source> with no src fires `error` too — every HLS slide before its fallback, and every
// slide unloadVideoElement() strips — so only a failure of the mp4 actually requested counts.
const onProgressiveSourceError = (event) => {
    const src = event.target.getAttribute('src')

    if (!src || src !== progressiveSrc.value) {
        return
    }

    markPlaybackFailed('source_error')
}

// First tap re-requests the same URL, which covers a transient network drop. A 474 needs a
// freshly signed URL, so the second tap — and any HLS slide, where load() would pull the MSE
// source out from under hls.js — reloads the page, which re-signs for the viewer's current IP.
const retryPlayback = () => {
    const video = videoRef.value

    if (playbackRetried.value || hlsInstance || !video) {
        window.location.reload()

        return
    }

    playbackRetried.value = true
    playbackFailed.value = false
    video.load()
    armPlaybackStallTimer()
    playVideo()
}

watch(() => props.shouldAutoplay, (current) => {
    if (current) {
        armPlaybackStallTimer()
    } else {
        clearPlaybackStallTimer()
    }
}, { immediate: true })

const hasStartedPlaying = ref(false)
// When no poster, show video layer immediately; otherwise fade it in after first frame
const videoLayerVisible = computed(() =>
    !posterImageUrl.value || hasStartedPlaying.value
)

const containerStyle = computed(() => {
    // Desktop: fixed dimensions
    if (windowWidth.value >= 1024) {
        return {
            width: '560px',
            height: `${windowHeight.value}px`,
        }
    }

    // Mobile: use visual viewport height
    return {
        height: `${windowHeight.value}px`,
        width: '100%'
    }
})

// HLS segment-loading roles; syncHlsLoad() and the rest live in the HLS block below.
const hlsShouldLoad = computed(() => props.shouldAutoplay || props.preloadMode === 'auto')
const hlsShouldPrefetch = computed(() => props.prefetchHls && !hlsShouldLoad.value && currentSlidePlaying.value)

// Declared before the shouldAutoplay watcher on purpose: watchers flush in declaration order,
// so a slide that just left stops loading before that watcher seeks it back to 0, and the
// seek fetches nothing.
watch([hlsShouldLoad, hlsShouldPrefetch], () => {
    syncHlsLoad()
})

// The feed keeps slides mounted across swipes instead of remounting them, so becoming or
// leaving current has to do what a remount would.
watch(() => props.shouldAutoplay, (newVal) => {
    if (!videoRef.value) {
        return
    }

    if (newVal) {
        // The slide was not remounted, so re-apply the mute state onMounted would have.
        isMuted.value = mutedStore.initialize(hasInteracted.value)
        videoRef.value.muted = isMuted.value

        syncHlsDebug()
        startStallWatchdog()
        playVideo()

        return
    }

    stopStallWatchdog()
    syncHlsDebug()
    pauseVideo()

    // Cleared synchronously here, while the new current only sets it from its async @playing, so
    // the next slide prefetches only once the new current is on screen.
    currentSlidePlaying.value = false

    // A slide that stops being current must never carry a latched spinner or play cue forward.
    isBuffering.value = false
    autoplayBlocked.value = false

    // Restart-from-zero + poster-again; seeking to 0 does not discard the buffered ranges.
    videoRef.value.currentTime = 0
    videoProgress.value = 0
    hasStartedPlaying.value = false
    isPaused.value = false
})

// Track video progress
const startProgressTracking = () => {
    stopProgressTracking()

    progressInterval = setInterval(() => {
        if (videoRef.value && videoRef.value.duration) {
            videoProgress.value = (videoRef.value.currentTime / videoRef.value.duration) * 100
        }
    }, 100)
}

const stopProgressTracking = () => {
    if (progressInterval) {
        clearInterval(progressInterval)
        progressInterval = null
    }
}

const onVideoPlay = () => {
    isPlaying.value = true
    isPaused.value = false
    isBuffering.value = false
    startProgressTracking()

    // Delay poster fade until first video frame has painted to reduce flicker
    requestAnimationFrame(() => {
        requestAnimationFrame(() => {
            hasStartedPlaying.value = true
        })
    })
}

const onVideoPlaying = () => {
    autoplayBlocked.value = false

    // @playing, not @play: @play fires as soon as play() is called, before any frame, so the next
    // slide's HLS prefetch (mp4 or HLS current alike) could race this clip's first fragment.
    if (props.shouldAutoplay) {
        currentSlidePlaying.value = true
    }
}

// Only the browser's autoplay refusal counts. An AbortError is a load() or pause() superseding
// the call (hls.js attaching, a swipe), and a later play() follows it.
const markAutoplayBlocked = (error) => {
    if (error?.name === 'NotAllowedError' && props.shouldAutoplay) {
        autoplayBlocked.value = true
    }
}

const playVideo = async () => {
    if (!videoRef.value) return

    try {
        await videoRef.value.play()
        // onVideoPlay runs via @play handler
    } catch (error) {
        // A load() or attachMedia superseded this call; the play() that follows them wins.
        if (error?.name === 'AbortError') {
            return
        }

        console.error('Play failed:', error)
        // If play fails, try muted
        if (videoRef.value && !videoRef.value.muted) {
            isMuted.value = true
            videoRef.value.muted = true
            mutedStore.setMuted(true)
            try {
                await videoRef.value.play()
                // onVideoPlay runs via @play handler
            } catch (err) {
                if (err?.name === 'AbortError') {
                    return
                }

                console.error('Muted play also failed:', err)
                markAutoplayBlocked(err)
            }
        } else {
            markAutoplayBlocked(error)
        }
    }
}

const pauseVideo = () => {
    if (!videoRef.value) return

    videoRef.value.pause()
    isPlaying.value = false
    isPaused.value = true
    stopProgressTracking()
}

// Handle video click for play/pause
const handleVideoClick = (e) => {
    // Don't toggle if clicking on buttons/controls
    if (e.target.tagName === 'BUTTON' || e.target.closest('button') || e.target.closest('a')) {
        return
    }

    if (!props.shouldVideoBeLocked && videoRef.value) {
        if (videoRef.value.paused) {
            playVideo()
        } else {
            pauseVideo()
        }
    }
}

// Handle mute toggle
const handleToggleMute = () => {
    if (videoRef.value) {
        isMuted.value = !isMuted.value
        videoRef.value.muted = isMuted.value
        mutedStore.setMuted(isMuted.value)
    }
}

// Handle seeking
const handleSeek = (percentage) => {
    if (videoRef.value && videoRef.value.duration) {
        const wasPlaying = !videoRef.value.paused // Track playback state before seeking
        const newTime = (percentage / 100) * videoRef.value.duration

        videoRef.value.currentTime = newTime
        videoProgress.value = percentage

        // Force resume if it was playing
        if (wasPlaying) {
            // Use nextTick or setTimeout to ensure the seek completes first
            setTimeout(() => {
                if (videoRef.value && videoRef.value.paused) {
                    playVideo()
                }
            }, 50)
        }
    }
}

// Update dimensions on resize/orientation change so containerStyle stays in sync
const updateWindowSize = () => {
    windowWidth.value = window.innerWidth
    // Use visualViewport if available (more accurate for mobile browsers)
    windowHeight.value = window.visualViewport?.height ?? window.innerHeight
}

/*
 * HLS. Only a clip whose renditions were delivered carries `videoUrl_hls`; any other clip
 * never enters any of this and keeps the mp4 <source> untouched. A locked slide plays the
 * lock poster, so it gets no adaptive source and never downloads the hls.js chunk either.
 */
const hlsH264Url = computed(() => props.shouldVideoBeLocked ? '' : (props.videoData?.videoUrl_hls || ''))

// The HEVC master (docs/nex-9360-clip-hevc-renditions.md), on its own host. Offered only beside
// an H.264 one, and only to a browser that says it can decode HEVC; a payload without it never
// runs the probe.
const hlsH265Url = computed(() => props.shouldVideoBeLocked ? '' : (props.videoData?.videoUrl_hls_h265 || ''))

// Set once the HEVC master has failed and the clip moved to H.264; never reset, like `hlsFellBack`.
const hlsH265Failed = ref(false)

// `?hls_codec=` (debug gate only, null for every real viewer) overrides the decoder probe, so one
// clip can be tested on either master in the same browser. A forced H.265 still falls back.
const hlsUrl = computed(() => {
    const forced = forcedCodec()
    const wantsHevc = forced === null ? canDecodeHevc() : forced === 'h265'

    if (hlsH264Url.value && hlsH265Url.value && !hlsH265Failed.value && wantsHevc) {
        return hlsH265Url.value
    }

    return hlsH264Url.value
})

// true while the clip is on its HEVC master, which is the one case a failure retries on H.264.
const onHevcMaster = () => Boolean(hlsH265Url.value) && !hlsH265Failed.value && hlsUrl.value === hlsH265Url.value

// Set once the adaptive source is abandoned; never reset, like `hlsMedia`.
const hlsFellBack = ref(false)

// An HLS slide renders its mp4 <source> without a src until it falls back. hls.js attaches
// only after its chunk lands, and an autoplaying element would otherwise start downloading
// the mp4 in that gap.
const progressiveSrc = computed(() => {
    if (hlsUrl.value && !hlsFellBack.value) {
        return null
    }

    return props.shouldVideoBeLocked
        ? props.videoData.lock.videoUrl
        : props.videoData.videoUrl_mp4_unbranded || props.videoData.videoUrl_mp4
})

let hlsInstance = null
// The element the adaptive source is currently bound to. `renderVideo` puts the <video>
// behind a v-if, so it can arrive after mount and vanish before unmount, and it also keeps
// the attach from running twice for one element.
let hlsMedia = null
// Segment loading is explicit (`autoStartLoad: false`): every slide loads its manifest, but
// only the current one streams, and the next one buffers a single fragment.
let hlsManifestParsed = false
let hlsLoadState = 'idle' // 'idle' | 'prefetching' | 'prefetched' | 'loading'
let hlsMediaRecoveryTried = false

// Mid-playback stall watchdog (hls.js path only), one per <video>, so its lifetime totals survive
// the H.265 → H.264 re-attach. It runs only while this slide streams as the current one.
let stallWatchdog = null
let stallWatchdogMedia = null
let stallWatchdogRunning = false

// Clears window.__clipHls when this slide still owns it; replaced by every registration.
let unregisterHlsDebug = () => {}

// The rendition rungs hls.js parsed from the master (highest first) and the one it is playing.
// Empty on native HLS (Safari/iOS) and mp4, which is what hides the feed's quality picker.
const qualityLevels = ref([])
const activeHeight = ref(null)

// A rung is named by its short side: the portrait 1080x1920 rendition is "1080p", as in the
// render ladder (config/clip-render.php), not "1920p".
const rungOf = (level) => {
    const rung = Math.min(level.width || Infinity, level.height || Infinity)

    return Number.isFinite(rung) ? rung : null
}

// The exact rung, else the closest one below it (HEVC and H.264 ladders need not match), else
// the lowest; -1 hands control back to ABR.
const levelIndexForHeight = (height) => {
    if (height === null || !hlsInstance) {
        return -1
    }

    let best = -1

    hlsInstance.levels.forEach((level, index) => {
        if (rungOf(level) <= height && (best === -1 || rungOf(level) > rungOf(hlsInstance.levels[best]))) {
            best = index
        }
    })

    if (best !== -1) {
        return best
    }

    return hlsInstance.levels.reduce((low, level, index) => (rungOf(level) < rungOf(hlsInstance.levels[low]) ? index : low), 0)
}

// A pick on the streaming slide switches now (currentLevel flushes the buffer). Back to Auto,
// or on any other slide, only the level the next load uses changes, so nothing is thrown away.
const applyPreferredLevel = () => {
    if (!hlsInstance || !hlsManifestParsed) {
        return
    }

    const index = levelIndexForHeight(props.preferredHeight)

    if (index !== -1 && hlsLoadState === 'loading') {
        hlsInstance.currentLevel = index
    } else {
        hlsInstance.loadLevel = index
    }
}

// ABR's ceiling from the player box (capLevelForPlayer). It bounds auto mode only, so the quality
// picker's manual level is never capped.
const applyLevelCap = () => {
    const video = videoRef.value

    if (!hlsInstance || !hlsManifestParsed || !video) {
        return
    }

    hlsInstance.autoLevelCapping = capLevelForPlayer(
        hlsInstance.levels,
        video.clientWidth,
        video.clientHeight,
        window.devicePixelRatio || 1
    )
}

useResizeObserver(videoRef, applyLevelCap)

const hlsDebugPath = () => {
    if (hlsInstance) {
        return 'hls.js'
    }

    const video = videoRef.value

    return video && hlsUrl.value && !hlsFellBack.value && video.getAttribute('src') === hlsUrl.value ? 'native' : 'mp4'
}

const hlsDebugCodec = () => (onHevcMaster() ? 'h265' : 'h264')

// Only the current slide owns window.__clipHls; a no-op unless the debug gate is open.
function syncHlsDebug() {
    unregisterHlsDebug()
    unregisterHlsDebug = () => {}

    if (!props.shouldAutoplay || !hlsUrl.value || !videoRef.value) {
        return
    }

    unregisterHlsDebug = registerHlsDebug({
        slug: props.videoData?.slug,
        hls: hlsInstance,
        video: videoRef.value,
        path: hlsDebugPath,
        codec: hlsDebugCodec,
        watchdog: stallWatchdog,
    })
}

// A fresh nudge budget per load (new source, codec fallback, re-attach, becoming current again).
function startStallWatchdog() {
    if (!stallWatchdog || hlsLoadState !== 'loading' || !props.shouldAutoplay) {
        return
    }

    stallWatchdog.stop()
    stallWatchdog.reset()
    stallWatchdog.start()
    stallWatchdogRunning = true
}

function stopStallWatchdog() {
    stallWatchdog?.stop()
    stallWatchdogRunning = false
}

// Times the stall window from a fragment landing rather than from the last advance, so a
// starvation stall is left to Chrome for a full window once data is back; the frozen join that
// never resumes is still nudged a window later. stop/start keeps the nudge budget.
function restartStallWindow() {
    if (stallWatchdogRunning) {
        stallWatchdog.stop()
        stallWatchdog.start()
    }
}

// Past the watchdog's nudge budget: the same path a fatal hls.js error takes. Only a fall all the
// way to the mp4 is a playback failure worth reporting; the mp4 then plays, so no failed state.
const onStallGiveUp = (video, info) => {
    stallWatchdogRunning = false
    recordHlsDebugEvent('fallback', info)

    if (!hlsInstance || videoRef.value !== video) {
        return
    }

    if (!onHevcMaster()) {
        trackClipPlaybackFailed('stall_watchdog', playbackRetried.value, props.videoData)
    }

    fallbackFromHls(video)
}

// Start on the rung the page last measured rather than on the instance's own seed.
const applyRecalledBandwidthEstimate = () => {
    const recalled = recalledBandwidthEstimate()

    if (recalled === null) {
        return
    }

    try {
        hlsInstance.bandwidthEstimate = recalled
    } catch (error) {
        console.error('HLS bandwidth seed failed:', error)
    }
}

// The single place that starts and stops segment loading, from the slide's current role.
const syncHlsLoad = () => {
    if (!hlsInstance || !hlsManifestParsed) {
        return
    }

    if (hlsShouldLoad.value) {
        if (hlsLoadState !== 'loading') {
            applyRecalledBandwidthEstimate()
            hlsInstance.config.maxMaxBufferLength = buildClipHlsConfig().maxMaxBufferLength
            hlsInstance.startLoad(-1)
            hlsLoadState = 'loading'
            startStallWatchdog()
        }

        return
    }

    if (hlsShouldPrefetch.value && prefetchAllowed()) {
        if (hlsLoadState === 'idle') {
            applyRecalledBandwidthEstimate()
            // hls.js requests the next fragment from its own FRAG_BUFFERED handler, before ours
            // can stopLoad(), so the one-fragment budget is enforced by the buffer target too.
            hlsInstance.config.maxMaxBufferLength = PREFETCH_MAX_BUFFER_SECONDS
            hlsInstance.startLoad(0)
            hlsLoadState = 'prefetching'

            return
        }

        if (hlsLoadState === 'prefetching') {
            return
        }
    }

    // The slide lost its role. Whatever is already buffered stays for a later swipe back.
    if (hlsLoadState === 'loading' || hlsLoadState === 'prefetching') {
        hlsInstance.stopLoad()
        hlsLoadState = 'idle'
        stopStallWatchdog()
    }
}

const destroyHlsInstance = () => {
    if (!hlsInstance) {
        return
    }

    stopStallWatchdog()
    unregisterHlsDebug()
    unregisterHlsDebug = () => {}

    if (hlsLoadState === 'loading') {
        rememberBandwidthEstimate(hlsInstance.bandwidthEstimate)
    }

    try {
        hlsInstance.destroy()
    } catch (error) {
        console.error('HLS destroy failed:', error)
    }

    hlsInstance = null
    hlsManifestParsed = false
    hlsLoadState = 'idle'
    hlsMediaRecoveryTried = false
    qualityLevels.value = []
    activeHeight.value = null
}

// Element teardown: forget the binding too, so a later element attaches fresh.
const detachHls = () => {
    destroyHlsInstance()
    hlsMedia = null
    stopStallWatchdog()
    stallWatchdog = null
    stallWatchdogMedia = null
    unregisterHlsDebug()
    unregisterHlsDebug = () => {}
}

// The backward-compatibility promise: drop the adaptive source and let the mp4 <source> that
// is always in the template take over. `hlsMedia` stays set so a failed clip is not retried.
const fallbackToProgressive = async (video) => {
    destroyHlsInstance()

    // The <source> only gets its mp4 src on the next render.
    hlsFellBack.value = true
    await nextTick()

    if (videoRef.value !== video) {
        return
    }

    video.removeAttribute('src')
    video.load()
    syncHlsDebug()

    if (props.shouldAutoplay && !props.shouldVideoBeLocked) {
        playVideo()
    }
}

// A fatal failure on the HEVC master retries the clip once on the H.264 one; only a failure
// there (or on a clip that never had HEVC) reaches the mp4. Each step happens at most once.
const fallbackFromHls = async (video) => {
    if (!onHevcMaster()) {
        fallbackToProgressive(video)

        return
    }

    destroyHlsInstance()
    hlsMedia = null
    hlsH265Failed.value = true

    // hlsUrl now resolves to H.264 and the watch re-attaches; the explicit call covers the
    // case where the URL did not change, and is a no-op once the watch has run.
    await nextTick()

    if (videoRef.value === video) {
        attachHls()
    }
}

// Native HLS (Safari/iOS) reports a failed master only as an element error. Only an error
// while the element's own src is still the master is ours: the mp4 <source>, the hls.js MSE
// blob src and anything after the fallback removed src are all no-ops here.
const onVideoError = () => {
    const video = videoRef.value

    if (!video || hlsInstance || !hlsUrl.value || video.getAttribute('src') !== hlsUrl.value) {
        return
    }

    fallbackFromHls(video)
}

const attachHls = async () => {
    const video = videoRef.value

    if (!video || !hlsUrl.value || hlsMedia === video) {
        return
    }

    hlsMedia = video

    // Chromium 151 answers canPlayType "maybe" too, so checking it first skipped hls.js (and
    // every bit of its tuning) on Chrome. Only Apple stays native, for AirPlay and battery;
    // sending iOS through ManagedMediaSource instead is a deferred follow-up. An explicit src
    // wins over the child <source>, so the mp4 stays in the template as the fallback.
    const nativeHls = video.canPlayType('application/vnd.apple.mpegurl') !== ''

    if (nativeHls && preferNativeHls()) {
        video.src = hlsUrl.value
        syncHlsDebug()

        return
    }

    // No MSE means no hls.js: a non-Apple browser that still plays HLS natively does so,
    // anything else gets the mp4 without downloading the chunk.
    if (!mseAvailable()) {
        if (nativeHls) {
            video.src = hlsUrl.value
        } else {
            fallbackToProgressive(video)
        }

        return
    }

    try {
        // Inside the branch on purpose: the chunk never enters the main bundle and never
        // downloads for a viewer whose clips are all mp4.
        const { default: Hls } = await import('hls.js')

        // The slide can unmount, or lock, while the chunk is in flight.
        if (videoRef.value !== video || !hlsUrl.value) {
            return
        }

        if (!Hls.isSupported()) {
            fallbackToProgressive(video)

            return
        }

        destroyHlsInstance()

        // The capped loader exists only behind the debug gate; real viewers get hls.js's own.
        const debug = isHlsDebugEnabled({ isProduction: page.props.app?.isProduction })

        hlsInstance = new Hls({
            ...buildClipHlsConfig(),
            ...(debug ? { fLoader: createCappedLoader(Hls.DefaultConfig.loader) } : {}),
        })

        if (stallWatchdogMedia !== video) {
            stallWatchdog?.stop()
            stallWatchdog = createStallWatchdog(video, {
                onStall: (info) => recordHlsDebugEvent('stall', info),
                onNudge: (info) => recordHlsDebugEvent('nudge', info),
                onGiveUp: (info) => onStallGiveUp(video, info),
            })
            stallWatchdogMedia = video
        }

        // A new load: the nudge budget spent on the previous source does not carry over.
        stallWatchdog.reset()

        syncHlsDebug()

        hlsInstance.on(Hls.Events.ERROR, (event, data) => {
            if (!data?.fatal) {
                return
            }

            // A decode or append failure is often recoverable once; anything else goes to H.264
            // when this was the HEVC master, to mp4 otherwise.
            if (data.type === Hls.ErrorTypes.MEDIA_ERROR && !hlsMediaRecoveryTried && hlsInstance) {
                hlsMediaRecoveryTried = true
                hlsInstance.recoverMediaError()

                return
            }

            fallbackFromHls(video)
        })

        // onMounted's autoplay timer has long since fired by the time the manifest lands.
        hlsInstance.on(Hls.Events.MANIFEST_PARSED, () => {
            hlsManifestParsed = true
            qualityLevels.value = [...new Set(hlsInstance.levels.map(rungOf))]
                .filter(Boolean)
                .sort((a, b) => b - a)
            applyLevelCap()
            applyPreferredLevel()
            syncHlsLoad()

            if (props.shouldAutoplay && !props.shouldVideoBeLocked) {
                playVideo()
            }
        })

        hlsInstance.on(Hls.Events.LEVEL_SWITCHED, (event, data) => {
            const level = hlsInstance?.levels[data.level]
            activeHeight.value = level ? rungOf(level) : null
        })

        // One fragment is enough for the swipe to start from buffer; ABR fetches the rest.
        hlsInstance.on(Hls.Events.FRAG_BUFFERED, (event, data) => {
            if (data?.frag?.type === 'main' && hlsLoadState === 'prefetching' && hlsInstance) {
                hlsInstance.stopLoad()
                hlsLoadState = 'prefetched'
            }

            if (data?.frag?.type === 'main' && hlsLoadState === 'loading') {
                restartStallWindow()
            }
        })

        // A lone prefetched fragment is a poor sample, so only a streaming slide may overwrite
        // the page's estimate.
        hlsInstance.on(Hls.Events.FRAG_LOADED, () => {
            if (hlsLoadState === 'loading' && hlsInstance) {
                rememberBandwidthEstimate(hlsInstance.bandwidthEstimate)
            }
        })

        hlsInstance.attachMedia(video)
        hlsInstance.loadSource(hlsUrl.value)
    } catch (error) {
        console.error('HLS attach failed:', error)
        fallbackToProgressive(video)
    }
}

watch(() => props.preferredHeight, applyPreferredLevel)

watch([videoRef, hlsUrl], ([video], [previousVideo]) => {
    if (previousVideo && previousVideo !== video) {
        detachHls()
    }

    if (video && hlsUrl.value) {
        attachHls()
    }
})

onMounted(() => {
    window.addEventListener('resize', updateWindowSize)
    window.addEventListener('orientationchange', updateWindowSize)

    // Visual viewport is more accurate for mobile browsers with dynamic toolbars
    if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', updateWindowSize)
    }

    updateWindowSize()

    // Initialize mute state from store
    isMuted.value = mutedStore.initialize(hasInteracted.value)

    if (videoRef.value) {
        videoRef.value.muted = isMuted.value

        // Start playback if needed (@play in template handles onVideoPlay)
        if (props.shouldAutoplay && !props.shouldVideoBeLocked) {
            setTimeout(() => {
                // On hls.js before the master is parsed, attachMedia's load would only abort this
                // play(); MANIFEST_PARSED plays instead. Native HLS (src is the master) and mp4 play.
                const video = videoRef.value
                const awaitingHlsJs = hlsMedia !== null && hlsMedia === video && !hlsFellBack.value
                    && !hlsManifestParsed && video.getAttribute('src') !== hlsUrl.value

                if (!awaitingHlsJs) {
                    playVideo()
                }
            }, 150)
        }
    }
})

/** Unload video from memory: pause, clear src, release buffer. Critical on mobile after many swipes. */
function unloadVideoElement() {
    if (!videoRef.value) return
    const video = videoRef.value
    // Three slides are mounted at once, and a live Hls instance keeps fetching segments long
    // after its slide is gone.
    detachHls()
    video.pause()
    video.removeAttribute('src')
    const source = video.querySelector('source')
    if (source) {
        source.removeAttribute('src')
    }
    video.load()
}

onUnmounted(() => {
    clearPlaybackStallTimer()
    stopProgressTracking()
    autoplayBlocked.value = false

    if (props.shouldAutoplay) {
        currentSlidePlaying.value = false
    }

    window.removeEventListener('resize', updateWindowSize)
    window.removeEventListener('orientationchange', updateWindowSize)
    if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', updateWindowSize)
    }

    unloadVideoElement()
})

// The feed's mute button, seek slider and quality picker drive the current slide through these.
defineExpose({
    videoProgress,
    isMuted,
    handleToggleMute,
    handleSeek,
    qualityLevels,
    activeHeight,
})
</script>
